import { Typography, Button, Icon, Scrollbar, Alert } from "@ann/web-components";
import {
    DocumentsContentWrapper, AccessOptionsBar, AccessOptionsBarLeft, AccessOptionsBarRight,
    CustomModalContentContainer, DocumentDetailWrapper, DocumentsContentWrapperScrollbar
} from "./DocumentsView.styled";
import DocumentCard from "../../components/Cards/DocumentCard";
import React, { useState, useEffect, useMemo, useCallback, useTransition, useDeferredValue } from "react";
import DocumentItem from "../../components/ListItem/DocumentItem";
import { useOutletContext } from "react-router-dom";
import RemoveAccessModal from "../../components/Modals/RemoveAccessModal";
import { useStore } from "../../../../../../../hooks";
import CircularProgress from '@mui/material/CircularProgress';
import { ModalBase } from "../../../../components/Modal";
import { observer } from "mobx-react";

const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Track renders
let renderCount = 0;

const DocumentsView = observer(() => {
    renderCount++;
    console.time(`⏱️ DocumentsView Render #${renderCount}`);
    console.log(`🏁 DocumentsView: RENDER #${renderCount} started`);
    
    const { viewMode, searchTerm, setShowSlideoutDocumentDetail, setSelectedDocument } = useOutletContext()
    console.log('📥 DocumentsView: Got outlet context');
    
    const [selectMode, setSelectMode] = useState(false);
    const [selectedItemsSet, setSelectedItemsSet] = useState(new Set());  // ✅ PERFORMANCE: Use Set
    const [isOpen, setIsOpen] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // ✅ PERFORMANCE: Start with 0, then progressively load
    const [displayedCount, setDisplayedCount] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const { documentStore, playgroundConfigStore } = useStore();
    console.log('📊 DocumentsView: Got stores');

    // Debug: Log component lifecycle
    useEffect(() => {
        const mountTime = performance.now();
        console.log('🎬 DocumentsView: Component mounted');
        return () => {
            const unmountTime = performance.now();
            const totalMountDuration = unmountTime - mountTime;
            console.log(`🔚 DocumentsView: Component unmounted (was mounted for ${totalMountDuration.toFixed(2)}ms)`);
        };
    }, []);

    // Debug: Log when render completes
    useEffect(() => {
        console.log(`🎨 DocumentsView Render #${renderCount}: Paint/Commit completed`);
    });

    // ✅ PERFORMANCE: Convert Set to Array for UI compatibility
    const selectedItems = useMemo(() => Array.from(selectedItemsSet), [selectedItemsSet]);

    // ✅ PERFORMANCE: O(1) selection toggle
    const handleSelectItem = useCallback((itemId) => {
        setSelectedItemsSet(prevSet => {
            const newSet = new Set(prevSet);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    }, []);

    const getSelectedDocumentsInfo = () => {
        return selectedItems.map(id => {
            const doc = filteredDocuments.find(d => d.uuid === id);
            return doc ? doc.name : `Documento ${id}`;
        });
    }

    const onClose = useCallback(() => {
        setIsOpen(false);
        setSelectedItemsSet(new Set());  // Clear Set
        setSelectMode(false);
    }, []);

    const handleCloseAlert = () => {
        setAlert(null);
    }

    const handleDeleteSelected = async () => {
        try {
            const selectedDocsInfo = getSelectedDocumentsInfo();
            console.log('Deleting documents:', selectedItems);
            console.log('Document names:', selectedDocsInfo);

            const agentUuid = playgroundConfigStore.agentStore.uuid;
            const tenantId = "58145bef-4f71-468a-bded-1823d2a08f58";

            if (!agentUuid) {
                throw new Error('Agent UUID not found');
            }

            // Delete multiple documents
            const result = await documentStore.deleteMultipleDocuments(
                selectedItems,
                agentUuid,
                tenantId
            );

            // Show appropriate message based on results
            if (result.failedCount === 0) {
                const successMessage = selectedItems.length === 1
                    ? `Document "${selectedDocsInfo[0]}" deleted successfully`
                    : `${selectedItems.length} documents deleted successfully`;

                setAlert({
                    title: "Success",
                    message: successMessage,
                    severity: "success"
                });
            } else if (result.successCount > 0) {
                // Partial success
                setAlert({
                    title: "Partial Success",
                    message: `${result.successCount} document(s) deleted successfully, ${result.failedCount} failed`,
                    severity: "warning"
                });
            } else {
                // All failed
                throw new Error(`Failed to delete document(s): ${result.errors[0]?.error || 'Unknown error'}`);
            }

            setSelectedItemsSet(new Set());  // Clear Set
            setSelectMode(false);
            setShowDeleteModal(false);

            // Remove deleted documents from local list (no API call needed)
            documentStore.removeMultipleDocumentsFromList(selectedItems);

        } catch (error) {
            let errorMessage = 'Error deleting documents';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlert({
                title: "Error",
                message: errorMessage,
                severity: "error"
            });
        }
    }

    const handleCancelSelection = useCallback(() => {
        setSelectedItemsSet(new Set());  // Clear Set
        setSelectMode(false);
    }, []);

    const handleSingleDeleteSuccess = useCallback((deletedDocumentUuid) => {
        // Remove deleted document from local list (no API call needed)
        documentStore.removeDocumentFromList(deletedDocumentUuid);
    }, [documentStore]);




    useEffect(() => {
        const fetchDocuments = async () => {
            console.time('⏱️ DocumentsView Fetch Effect');
            const vaultUuid = playgroundConfigStore.agentStore.vaultUuid;

            if (!vaultUuid) {
                console.log('⚠️ vaultUuid not available yet, skipping fetch');
                console.timeEnd('⏱️ DocumentsView Fetch Effect');
                return;
            }

            // ✅ PERFORMANCE: Only fetch if not already loaded or if vault changed
            if (vaultUuid !== playgroundConfigStore.agentStore.lastLoadedVaultId) {
                console.log('📥 Fetching documents for vault:', vaultUuid);
                console.time('⏱️ API Call fetchDocumentList');
                
                try {
                    const requestBody = {
                        tenantId: "1",
                        vaultUuid: vaultUuid,
                        page_size: 1000
                    };

                    await documentStore.fetchDocumentList(requestBody);
                    console.timeEnd('⏱️ API Call fetchDocumentList');
                    playgroundConfigStore.agentStore.lastLoadedVaultId = vaultUuid;
                    console.log('✅ Documents loaded and cached, count:', documentStore.documentList.data?.length || 0);
                    
                } catch (error) {
                    console.timeEnd('⏱️ API Call fetchDocumentList');
                    let errorMessage = 'Error fetching documents';

                    if (error.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }

                    setAlert({
                        title: "Error",
                        message: errorMessage,
                        severity: "error"
                    });
                }
            } else {
                console.log('✅ Using cached documents for vault:', vaultUuid, '- count:', documentStore.documentList.data?.length || 0);
            }
            
            console.timeEnd('⏱️ DocumentsView Fetch Effect');
        };

        fetchDocuments();
    }, [playgroundConfigStore.agentStore.vaultUuid]);

    const documents = documentStore.documentList.data || [];
    const totalItems = documentStore.documentList.meta?.total_items || 0;

    // ✅ PERFORMANCE: Defer heavy filtering to not block UI
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const filteredDocuments = useMemo(() => {
        if (!deferredSearchTerm || deferredSearchTerm.trim() === '') {
            return documents;
        }

        const searchLower = deferredSearchTerm.toLowerCase();
        return documents.filter(document =>
            document.name?.toLowerCase().includes(searchLower) ||
            document.type?.toLowerCase().includes(searchLower) ||
            document.uuid?.toLowerCase().includes(searchLower) ||
            document.state?.toLowerCase().includes(searchLower) ||
            document.metadata?.owner?.toLowerCase().includes(searchLower) ||
            document.metadata?.version?.toLowerCase().includes(searchLower) ||
            document.metadata?.status?.toLowerCase().includes(searchLower)
        );
    }, [documents, deferredSearchTerm]);

    const filteredTotalItems = filteredDocuments.length;

    // ✅ PERFORMANCE: Only render subset of documents for fast initial display
    const displayedDocuments = useMemo(() => {
        return filteredDocuments.slice(0, displayedCount);
    }, [filteredDocuments, displayedCount]);

    const hasMore = filteredDocuments.length > displayedCount;

    // ✅ PERFORMANCE: Load more callback
    const handleLoadMore = useCallback(() => {
        setDisplayedCount(prev => Math.min(prev + 20, filteredDocuments.length));
    }, [filteredDocuments.length]);

    // ✅ PERFORMANCE: Load immediately after mount using requestAnimationFrame
    useEffect(() => {
        if (isInitialLoad && !documentStore.isLoadingDocuments) {
            console.log('🚀 DocumentsView: Starting immediate load...');
            
            // Use requestAnimationFrame for immediate but non-blocking load
            requestAnimationFrame(() => {
                console.log('📦 DocumentsView: Loading 15 items...');
                setDisplayedCount(15);
                setIsInitialLoad(false);
            });
        }
    }, [isInitialLoad, documentStore.isLoadingDocuments]);

    // Reset displayed count when search changes
    useEffect(() => {
        setDisplayedCount(10);
        setIsInitialLoad(true);
    }, [deferredSearchTerm]);

    console.timeEnd(`⏱️ DocumentsView Render #${renderCount}`);
    console.log(`✅ DocumentsView Render #${renderCount}: Component body completed`);
    console.log(`📊 DocumentsView Render #${renderCount}: Ready to render`, displayedDocuments.length, 'documents');

    if (documentStore.isLoadingDocuments) {
        console.log('⏳ DocumentsView: Showing loading spinner - fetching data');
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </div>
        );
    }

    // ✅ PERFORMANCE: Show loading state while items are being progressively loaded
    if (isInitialLoad && displayedCount === 0) {
        console.log('⏳ DocumentsView: Initial load - showing placeholder');
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </div>
        );
    }

    if (documentStore.isErrorLoadingDocuments) {
        console.log('❌ DocumentsView: Showing error state');
        console.log('⏱️ Returning early - error state');
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography category="body" size="medium" weight="regular" color="error">
                    {documentStore.isDocumentsErrorMessage}
                </Typography>
            </div>
        );
    }

    console.log('🎨 Starting JSX construction for', displayedDocuments.length, 'documents');

    const selectedDocuments = () => {
        return (
            <CustomModalContentContainer>
                {selectedItems.length > 0 ? (
                    selectedItems.map((itemId, index) => {
                        const document = filteredDocuments.find(doc => doc.uuid === itemId);
                        return (
                            <DocumentDetailWrapper key={`${itemId}-${index}`}>
                                <Icon name="description" />
                                <Typography category="body" size="medium" weight="regular" color="body">
                                    {document ? document.name : `Document ${index}`}
                                </Typography>
                            </DocumentDetailWrapper>
                        );
                    })
                ) : (
                    <Typography category="body" size="medium" weight="regular" color="body">
                        No documents selected
                    </Typography>
                )}
            </CustomModalContentContainer>
        )
    };

    return (
        <>
            {alert && (
                <Alert
                    title={alert.message}
                    severity={alert.severity}
                    onClose={handleCloseAlert}
                    maxWidth="400px"
                    closable={true}
                    autoClose={true}
                    autoCloseDelay={3000}
                />
            )}

            <RemoveAccessModal isOpen={isOpen} onClose={onClose} />
            <AccessOptionsBar>
                <AccessOptionsBarLeft>
                    {selectMode ? (
                        <>                        <Button
                            label={`Delete (${selectedItems.length})`}
                            size="medium"
                            type="secondary"
                            disabled={selectedItems.length === 0 || documentStore.isDeletingDocument}
                            onClick={() => setShowDeleteModal(true)}
                        />
                            <Button
                                label="Cancel"
                                size="medium"
                                type="secondary"
                                onClick={handleCancelSelection}
                            />
                        </>
                    ) : (
                        <Button
                            label="Select"
                            size="medium"
                            type="secondary"
                            onClick={() => setSelectMode(true)}
                        />

                    )}


                </AccessOptionsBarLeft>
                <AccessOptionsBarRight>
                    <Typography
                        category="body"
                        size="medium"
                        weight="regular"
                        color="body"
                    >
                        {filteredTotalItems} Data Sources
                    </Typography>
                </AccessOptionsBarRight>
            </AccessOptionsBar>

            <DocumentsContentWrapperScrollbar>
                <Scrollbar size="small">
                    <DocumentsContentWrapper>
                        {viewMode === 'grid' ? (
                            <>
                                {displayedDocuments.map((document) => (
                                    <DocumentCard
                                        key={document.uuid}
                                        uuid={document.uuid}
                                        name={truncateText(document.name)}
                                        state={document.state}
                                        meta={{
                                            owner: "System",
                                            version: "v1.0",
                                            status: "Active",
                                            createdAt: new Date().toISOString().split('T')[0],
                                        }}
                                        selectMode={selectMode}
                                        onSelect={handleSelectItem}
                                        selectedItems={selectedItems}
                                        onDeleteSuccess={handleSingleDeleteSuccess}
                                        setShowSlideoutDocumentDetail={setShowSlideoutDocumentDetail}
                                        setSelectedDocument={setSelectedDocument}
                                    />
                                ))}
                                {hasMore && (
                                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                        <Button
                                            label={`Load More (${filteredDocuments.length - displayedCount} remaining)`}
                                            onClick={handleLoadMore}
                                            size="medium"
                                            type="secondary"
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {displayedDocuments.map((document) => (
                                    <DocumentItem
                                        key={document.uuid}
                                        uuid={document.uuid}
                                        name={truncateText(document.name)}
                                        meta={{
                                            owner: "System",
                                            version: "v1.0",
                                            status: "Active",
                                            createdAt: new Date().toISOString().split('T')[0],
                                        }}
                                        selectMode={selectMode}
                                        onSelect={handleSelectItem}
                                        selectedItems={selectedItems}
                                        onDeleteSuccess={handleSingleDeleteSuccess}
                                        setShowSlideoutDocumentDetail={setShowSlideoutDocumentDetail}
                                        setSelectedDocument={setSelectedDocument}
                                    />
                                ))}
                                {hasMore && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                        <Button
                                            label={`Load More (${filteredDocuments.length - displayedCount} remaining)`}
                                            onClick={handleLoadMore}
                                            size="medium"
                                            type="secondary"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </DocumentsContentWrapper>
                </Scrollbar>
            </DocumentsContentWrapperScrollbar>

            {showDeleteModal && (
                <ModalBase
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onClick={handleDeleteSelected}
                    title={selectedItems.length === 1 ? `Are you sure you want to delete this file?` : `Are you sure you want to delete these ${selectedItems.length} files?`}
                    description={"This action will unassign the selected file(s) from your agent's knowledge base and soft delete them."}
                    children={selectedDocuments()}
                    buttonTitle={documentStore.isDeletingDocument ? "Deleting..." : "Delete"}
                    disabled={documentStore.isDeletingDocument}
                    modalWidth="571px" />
            )}
        </>
    )
});

export default DocumentsView;